/**
 * Compare pages — single source of truth for /compare/* × 4 pages,
 * powering: hero TL;DR, dimension-by-dimension contrast table,
 * "when to use which" section, and per-page FAQPage schema.
 *
 * Writing rules (per docs/seo-geo-playbook.md §4.2):
 *   - First paragraph (`tldr`) = 60–120 char self-contained brand
 *     definition + this comparison's conclusion.
 *   - 5–7 dimension rows, each with a one-line "why it matters".
 *   - 5 page-specific FAQ items. Brand canonical terms only.
 */

import type { SeoFaqItem } from "./seo-content";

export type CompareSlug =
  | "hotline-vs-mcp"
  | "hotline-vs-skills"
  | "hotline-vs-api"
  | "hotline-vs-saas";

export type CompareDimension = {
  /** Dimension label (Chinese, brand canonical wording). */
  label: string;
  /** What CALL ANYTHING / Hotline does for this dimension. */
  hotline: string;
  /** What the compared technology does for this dimension. */
  other: string;
  /** One-sentence "why this matters" for OPC / Caller. */
  why: string;
};

export type ComparePage = {
  slug: CompareSlug;
  /** Full canonical URL path (with trailing slash). */
  path: `/compare/${CompareSlug}/`;
  /** Page <title> (≤ 60 chars to stay under SERP truncation). */
  title: string;
  /** Page <meta description> (≤ 160 chars). */
  description: string;
  /** Top eyebrow strip; appears above hero. */
  eyebrow: string;
  /** Display name for the OTHER side, e.g. "MCP Server". */
  otherName: string;
  /** Display color for the OTHER side card, brand-palette hex. */
  otherColor: string;
  /** TL;DR — 60–120 chars, self-contained. MUST mention CALL ANYTHING + Hotline + comparison conclusion. */
  tldr: string;
  /** 1–3 bullet conclusions for screenshot-worthy "key takeaways". */
  takeaways: string[];
  /** Dimension contrast rows. */
  dimensions: CompareDimension[];
  /** When to choose Hotline. */
  whenHotline: string;
  /** When to stick with the other approach. */
  whenOther: string;
  /** Page-specific FAQ (5 items). */
  faqs: SeoFaqItem[];
  /** Recommended next routes (used in CTA section). */
  nextLinks: Array<{ href: string; label: string; tone?: "primary" | "secondary" }>;
};

const NEXT_LINKS_DEFAULT: ComparePage["nextLinks"] = [
  { href: "/docs/quick-start-responder", label: "5 分钟开通你的第一根 Hotline", tone: "primary" },
  { href: "/docs/quick-start-caller", label: "5 分钟把 Hotline 接进 Agent", tone: "secondary" },
  { href: "/marketplace", label: "浏览 Marketplace", tone: "secondary" },
];

export const COMPARE_PAGES: ComparePage[] = [
  // 1. Hotline vs MCP -------------------------------------------------------
  {
    slug: "hotline-vs-mcp",
    path: "/compare/hotline-vs-mcp/",
    title: "Hotline vs MCP Server — CALL ANYTHING",
    description:
      "MCP 适合让 Agent runtime 调用工具；Hotline 在此之上补齐产品化层——身份、发现、版本、评分与结算。一根 Hotline 可以同时暴露 MCP surface，两者互补。",
    eyebrow: "Compare · Hotline vs MCP",
    otherName: "MCP Server",
    otherColor: "#FACC15",
    tldr:
      "MCP 处理 tool access——Agent runtime 如何发现并调用工具。Hotline 处理 capability business shape——能力如何被产品化、被发现、被结算。一根 Hotline 可以暴露 MCP surface，但 product、routing、settlement 是 Hotline 层的事。它们是互补的，不是替代关系。",
    takeaways: [
      "MCP 解决 tool access，Hotline 解决能力产品化——它们是互补关系。",
      "Hotline 可以暴露 MCP surface，产品化与路由、结算在 Hotline 层完成。",
      "身份、发现、版本、评分和结算通常需要在 MCP 之上由产品层补齐，Hotline 提供了这一层。",
    ],
    dimensions: [
      {
        label: "解决的问题",
        hotline: "把能力包装成可发现、可调用、可结算的产品",
        other: "让 Agent runtime 连接并调用工具",
        why: "一个关注产品化形态，一个关注运行时接入管道——解决的问题不同。",
      },
      {
        label: "身份与信任",
        hotline: "协议层带 Responder 身份，支撑评分与信任评估",
        other: "适合让 Agent runtime 调用工具；身份、发现、评分和结算通常需要由产品层补齐",
        why: "能力走向市场时需要供给方身份，MCP 层不关注这件事。",
      },
      {
        label: "计费 / 结算",
        hotline: "按调用次数协议级结算到 Responder 账户",
        other: "专注于工具调用管道，计费逻辑留给上层产品自行实现",
        why: "需要直接变现的能力，需要协议级结算支撑。",
      },
      {
        label: "版本与发现",
        hotline: "Hotline ID 自带版本号，Marketplace 公开发现",
        other: "通过 Agent 配置或文档分发，发现机制由使用方决定",
        why: "Agent 团队可以像装 npm 包一样装 Hotline。",
      },
      {
        label: "可观测",
        hotline: "Caller / Responder Console 共享一份 ops 视图",
        other: "日志与可观测由各 runtime 自行实现",
        why: "跨方协作的事故复盘与对账需要统一的 ops 层。",
      },
      {
        label: "可同时使用？",
        hotline: "可以——同一根 Hotline 可暴露为 MCP server",
        other: "同上",
        why: "迁移成本低；现有 MCP server 可以通过 wrapper 获得 Hotline 产品层能力。",
      },
    ],
    whenHotline:
      "能力需要被产品化——需要身份、按次结算、版本管理与公开评分；或者你是 Agent 团队，想要一个可发现、可订阅版本的能力分发层。",
    whenOther:
      "只需要在 Agent runtime 里连接工具，不需要产品化包装（发现、结算、评分）。MCP 在 tool access 层简洁高效，直接使用即可。",
    faqs: [
      {
        question: "我已经有 MCP server，要重写吗？",
        answer:
          "不需要。client SDK 提供一层 wrapper，能把现有 MCP server 直接 wrap 成 Hotline，原 server 进程继续跑，Hotline 在外层补齐身份、计费、版本与评分。",
      },
      {
        question: "Agent runtime 已经支持 MCP，怎么调用 Hotline？",
        answer:
          "两条路径：1) Caller 通过 client SDK 拉 catalog 并发起调用；2) Hotline 把 MCP transport 当作一个适配器对外暴露，原生 MCP Agent 可以直接 invoke，但走的是 Hotline 协议结算。",
      },
      {
        question: "Hotline 的身份层是怎么工作的？",
        answer:
          "Responder 在 Platform 注册账户后获得长期 Hotline ID 与 Caller 可校验的签名。supervisor 启动时握手，每次调用带 token，协议层确认「谁在跑这根 Hotline」。",
      },
      {
        question: "Hotline 会影响 MCP 的简洁性吗？",
        answer:
          "不会。Hotline 把 MCP 看作一个 transport 适配器，Agent 端的 invoke 体验完全没变；产品化能力（结算、评分、版本）只在 catalog 与 Console 层面体现，不改变 MCP 层的调用方式。",
      },
      {
        question: "如果 MCP 协议升级，Hotline 跟得上吗？",
        answer:
          "client SDK 的 MCP 适配器与 Hotline 协议解耦：MCP 升级只影响适配器版本，Hotline 协议本身不动；Marketplace 上的 Hotline 不会因 MCP 升级而失效。",
      },
    ],
    nextLinks: NEXT_LINKS_DEFAULT,
  },

  // 2. Hotline vs Agent Skills ---------------------------------------------
  {
    slug: "hotline-vs-skills",
    path: "/compare/hotline-vs-skills/",
    title: "Hotline vs Agent Skills — CALL ANYTHING",
    description:
      "Skills 适合把说明、流程和偏好放进模型上下文，引导模型行为。Hotline 适合把能力产品化——私有实现不进入上下文，调用即结算。两者解决不同层级的问题，Skill 可以调用 Hotline。",
    eyebrow: "Compare · Hotline vs Skills",
    otherName: "Agent Skills",
    otherColor: "#8B5CF6",
    tldr:
      "Skills 适合行为注入和工作流指导——把说明、流程和偏好放进模型上下文。Hotline 适合能力产品化——私有实现不进入上下文，只暴露契约，调用即结算。Skills 可以调用 Hotline 作为执行后端，它们解决不同层级的问题。",
    takeaways: [
      "Skills 在模型上下文内引导行为，Hotline 在上下文外提供可调用能力——层级不同。",
      "Skills 可以调用 Hotline 作为执行后端，两者天然可以组合。",
      "商业化、版本管理、评分和结算需要在 Skill 之外另建，Hotline 提供了这一层。",
    ],
    dimensions: [
      {
        label: "执行位置",
        hotline: "Responder 本机执行，模型只见 input_schema / output_schema",
        other: "适合把说明、流程和偏好放进模型上下文，引导模型在上下文内执行",
        why: "执行位置决定了实现逻辑是否需要进入 prompt。",
      },
      {
        label: "上下文占用",
        hotline: "近零上下文占用，调用走协议外通道",
        other: "占用 prompt 空间和 token 额度，适合轻量级行为指导",
        why: "影响 Agent 在长会话里能装多少能力。",
      },
      {
        label: "责任边界",
        hotline: "Responder 对 result_package 负责；Caller 对 Agent 决策负责",
        other: "模型理解指令后自行执行，责任归属由 Agent 开发者管理",
        why: "需要明确责任归属的场景适合走协议化调用。",
      },
      {
        label: "商业化",
        hotline: "协议层按次结算，自动入账",
        other: "适合免费分享行为指导；商业化、版本、评分和结算需要在外层另建",
        why: "能力需要变现时，需要协议级结算支撑。",
      },
      {
        label: "更新与版本",
        hotline: "Hotline ID 带版本号，Caller 自选订阅",
        other: "通常跟 Agent 项目绑定，适合项目内的行为管理",
        why: "跨团队分发时需要独立的版本治理。",
      },
      {
        label: "可同时使用？",
        hotline: "可以——Hotline 可附带一份 SKILL.md 给模型当「调用提示」",
        other: "同上",
        why: "「Skill 引导模型去调 Hotline」是推荐的组合模式。",
      },
    ],
    whenHotline:
      "能力的核心实现需要保护、需要变现，或执行成本高不该由模型上下文承担——这些场景适合走 Hotline 协议化调用。",
    whenOther:
      "你的目标是引导模型行为——把说明、流程偏好放进上下文，让模型在项目内按指导执行。Skills 在行为注入和工作流指导上非常高效。",
    faqs: [
      {
        question: "我已经有 Skill，能和 Hotline 配合使用吗？",
        answer:
          "可以，这是推荐做法。Skill 部分负责引导模型「何时、为什么调用这个能力」，Hotline 负责实际执行与结算。需要保护或变现的部分迁到 Hotline，SKILL.md 改为调用提示即可。",
      },
      {
        question: "Hotline 会影响 Agent 的 prompt 吗？",
        answer:
          "不会。Hotline 的发现走 catalog API、调用走协议外通道；Agent 的 prompt 里只有「Hotline 名 + summary」级别的元信息，实现逻辑不进入上下文。",
      },
      {
        question: "什么场景下应该只用 Skill，不用 Hotline？",
        answer:
          "当你的目标是引导模型行为——比如写作风格、代码规范、流程偏好——这类指导天然适合放进 prompt 上下文，用 Skill 足矣。",
      },
      {
        question: "Hotline 的 supervisor 会增加部署复杂度吗？",
        answer:
          "supervisor 是一个 ~20MB 的本地常驻进程，全平台一行命令启动。它换来的是实现隔离、按次结算和跨 Hotline 共享 ops。",
      },
      {
        question: "两者的最佳组合模式是什么？",
        answer:
          "Skill 负责「告诉模型有这种能力可以调」，Hotline 负责「真正的执行 + 结算」。同一份 Hotline 可以附一段官方 Skill 模板，让 Caller 项目一键挂上。",
      },
    ],
    nextLinks: NEXT_LINKS_DEFAULT,
  },

  // 3. Hotline vs API / SaaS endpoints -------------------------------------
  {
    slug: "hotline-vs-api",
    path: "/compare/hotline-vs-api/",
    title: "Hotline vs API — CALL ANYTHING",
    description:
      "API 是服务接口标准，适合公司或平台提供稳定服务。Hotline 更关注个人或小团队如何把能力包装成可发现、可调用的契约。一根 Hotline 可以暴露为 OpenAPI endpoint。",
    eyebrow: "Compare · Hotline vs API",
    otherName: "传统 API",
    otherColor: "#3B82F6",
    tldr:
      "API 是服务接口标准，适合公司或平台提供稳定服务。Hotline 是个人或小团队能力的产品化契约——额外提供身份、发现、版本、计量与评分。一根 Hotline 可以暴露为 OpenAPI endpoint，它们面向不同的供给方群体，解决不同层级的问题。",
    takeaways: [
      "API 是接口标准，Hotline 是能力产品化契约——层级不同。",
      "一根 Hotline 可以暴露为 OpenAPI endpoint，两者可以共存。",
      "Hotline 额外提供身份、发现、版本、计量和评分，面向个人或小团队的能力分发。",
    ],
    dimensions: [
      {
        label: "面向的供给方",
        hotline: "个人或小团队；个人身份即可发布能力",
        other: "适合公司或平台提供稳定服务，通常有完整的商务与运维体系",
        why: "Hotline 让个人也能把能力产品化，API 更适合组织化的服务提供。",
      },
      {
        label: "Caller 接入方式",
        hotline: "拉 catalog → 直接 call；共享协议级 client",
        other: "读 OpenAPI / SDK → 写 client → 处理鉴权 / 限流",
        why: "调用多个供应商时，协议统一的接入成本更低。",
      },
      {
        label: "返回结构",
        hotline: "所有 Hotline 共享 result_package，Agent 只学一次",
        other: "每个 API 自定义 schema，灵活但需要逐个适配",
        why: "影响 Agent 能否泛化「调外部能力」这个动作。",
      },
      {
        label: "计费形态",
        hotline: "按次协议结算，分账自动到 Responder",
        other: "按 plan / quota / token 等形式，通过商业合同结算",
        why: "不同场景适合不同计费形态。",
      },
      {
        label: "鉴权 / 限流",
        hotline: "协议级 token + 限频策略，Console 一键配置",
        other: "各提供方自行实现鉴权与限流方案",
        why: "协议统一的鉴权降低双方的工程负担。",
      },
      {
        label: "可同时使用？",
        hotline: "可以——Hotline 内部实现可以是对一组 API 的封装",
        other: "同上",
        why: "「拿 API 做内部实现 + 对外发 Hotline」是常见模式。",
      },
    ],
    whenHotline:
      "你是个人或小团队，想把能力产品化——需要身份、发现、版本和按次结算；或者你是 Agent 团队，想用一份 client 统一调用多个供应商。",
    whenOther:
      "你是公司或平台，已有完整的 OpenAPI / SDK / 商务合同体系，面向稳定的客户群提供服务——API 在这个场景下成熟高效。",
    faqs: [
      {
        question: "我已经有 OpenAPI service，要重做吗？",
        answer:
          "不需要。Hotline 的 http adapter 直接对接已有 service：你写一份 input/output schema 与一段转发逻辑（通常 ≤ 50 行），原 service 继续跑，对外多了一层协议化的身份与结算。",
      },
      {
        question: "Caller 调 Hotline 时怎么鉴权？",
        answer:
          "client SDK 自动注入协议级 token，Responder 在 supervisor 端只看一个 caller_id 与权限标记，无需自己实现鉴权。Caller Console 可针对每根 Hotline 设置最大频率与每日预算。",
      },
      {
        question: "Hotline 能像 API 一样按 plan 收费吗？",
        answer:
          "现阶段 Hotline 的计费颗粒度是「按次 + 按 elapsed_ms」，不强制 plan 模型；如果你需要 plan / 包月，可以在自己的 Responder 实现里做 quota，对外仍然按次暴露。",
      },
      {
        question: "Hotline 有哪些防滥用机制？",
        answer:
          "三层：1) Caller 调用走协议级 token，匿名调用拒绝；2) Responder 可设置最低单价与最低预付；3) 调用前 Caller Console 显示 estimated cost 给用户确认。",
      },
      {
        question: "Hotline 能 stream 大数据吗？",
        answer:
          "可以。result_package 支持流式分包，Responder 可以分多次写 partial output，Caller 端按 chunk 解析。实际大数据场景通常走对象存储 + Hotline 返回 URL 的方式。",
      },
    ],
    nextLinks: NEXT_LINKS_DEFAULT,
  },

  // 4. Hotline vs SaaS Platform --------------------------------------------
  {
    slug: "hotline-vs-saas",
    path: "/compare/hotline-vs-saas/",
    title: "Hotline vs SaaS — CALL ANYTHING",
    description:
      "SaaS 适合公司提供持续运营的服务。Hotline 是个人能力的标准化封装——协议自带身份、计费、发现与控制台。它们面向不同的供给方群体，Hotline 协议也可以被 SaaS 公司采用。",
    eyebrow: "Compare · Hotline vs SaaS",
    otherName: "SaaS",
    otherColor: "#F97316",
    tldr:
      "SaaS 是公司提供持续运营的软件服务，有完整的账号、订阅与运维体系。Hotline 是个人能力的标准化封装，协议层自带身份、计费、发现与控制台。它们面向不同的供给方群体——SaaS 公司同样可以采用 Hotline 协议来暴露 Agent 可调用的能力。",
    takeaways: [
      "SaaS 面向公司级持续运营，Hotline 面向个人能力产品化——供给方群体不同。",
      "Hotline 协议可以被 SaaS 公司采用，作为 Agent 渠道暴露能力。",
      "Marketplace 与 Console 为个人能力提供开箱即用的分发与运营基础设施。",
    ],
    dimensions: [
      {
        label: "构建范围",
        hotline: "聚焦能力本身（input → output 的核心逻辑），协议提供周边基础设施",
        other: "完整的产品栈：账号、订阅、计费、对账、客服、后台",
        why: "不同规模的供给方，构建范围和资源投入不同。",
      },
      {
        label: "获客渠道",
        hotline: "Marketplace 公开发现 + Agent 自动撮合",
        other: "自建获客体系：SEO / 投放 / 内容 / 销售团队",
        why: "个人能力需要低成本的分发渠道。",
      },
      {
        label: "客户管理",
        hotline: "Caller Console 提供统一的客户侧控制台",
        other: "自建 customer portal，完全可定制",
        why: "不同场景对控制台的定制化程度要求不同。",
      },
      {
        label: "订单与发票",
        hotline: "由 Platform 汇总开票，Responder 拿净分账",
        other: "自行对接支付与发票系统，拥有完整财务控制",
        why: "个人供给方需要低门槛的财务方案。",
      },
      {
        label: "升级与迁移",
        hotline: "新版 Hotline 与老版同时跑，Caller 自主升级",
        other: "自行设计 versioning 与 deprecation 策略",
        why: "协议化的版本管理降低了个人供给方的升级风险。",
      },
      {
        label: "各自适合的场景",
        hotline: "「输入 → 输出」型能力，可被 Agent 直接调用",
        other: "B 端复杂工作流、多租户隔离、强 UI 体验",
        why: "两者面向不同的产品形态，不是替代关系。",
      },
    ],
    whenHotline:
      "你的能力是「输入 → 输出」型的，能被 Agent 直接调用；你是个人或小团队，想以最低工程成本对外提供能力。",
    whenOther:
      "你的产品是带界面的复杂工作流（如设计软件、文档协作、白板）、需要多租户隔离和完整的运营体系——SaaS 模式在这些场景下更成熟。",
    faqs: [
      {
        question: "SaaS 公司可以同时使用 Hotline 吗？",
        answer:
          "完全可以。把 SaaS 里「输入 → 输出」型的能力包成 Hotline 上架 Marketplace，等于在不影响主产品的前提下多开一条 Agent 渠道。",
      },
      {
        question: "Hotline 能定制 UI 吗？",
        answer:
          "Hotline 本身不带 UI；如果能力需要人工确认步骤，可在 Responder 端配合一个轻量审批页，把 URL 通过 result_package 返回给 Caller Console 嵌入。",
      },
      {
        question: "Marketplace 怎么帮供给方获客？",
        answer:
          "三层：1) 关键词搜索（Caller / 模型直接查能力）；2) 同类目排序（评分 / 调用量 / 响应速度）；3) Agent 自动撮合（Caller 配一个目标，模型从 catalog 找最合适的 Hotline 调用）。",
      },
      {
        question: "会被 Platform 锁定吗？",
        answer:
          "不会。Hotline 协议本身开源，client SDK 与自托管 Platform 也开源；任何时候都可以把 Hotline 迁到自托管 Platform，只是公网 Marketplace 的发现量会换成自己渠道。",
      },
      {
        question: "用了 Hotline 还能维护客户关系吗？",
        answer:
          "可以。Responder Console 看得到每个 Caller 的调用模式与频率，可以主动推送版本更新、做高价值 Caller 的回访。Marketplace 还会显示 Caller 的评分与留言。",
      },
    ],
    nextLinks: NEXT_LINKS_DEFAULT,
  },
];

export function getComparePageBySlug(slug: CompareSlug): ComparePage | undefined {
  return COMPARE_PAGES.find((page) => page.slug === slug);
}
